import {
  AdvancedFilter,
  CommonPageHeader,
  PageSwitcher,
  Search,
  TableHeaders,
  useAngularServices,
} from '@/react/components'
import { css } from '@emotion/react'
import { useEffect, useState } from 'react'
import { ThinCheckmark } from '@/react/componentAssets/ThinCheckmark'
import { useRouter } from '@/react/hooks'
import { ProjectRow } from './components/ProjectRow'
import actionIcon from '@/assets/icons/action-icon.svg'
import { hexToRGBA } from '@/utils/generalUtilities.js'
import { checkIsSafari } from '@/utils/checkIsSafari'
import { buildCombinedQueryString } from '@/utils/queryBuilder'
import { CurrentUserType } from '@/react/types/documentation'
import { ConfirmationModal } from '@/react/componentAssets/ConfirmationModal'
import { ConfigNavBar } from '../AdminCompanyProjectsConfig/AddProject/components/ConfigNavBar'
import { Project } from '@/react/types/project'

export const ProjectsList = () => {
  const { stateService } = useRouter()
  const { Api, CurrentUser } = useAngularServices()

  const [search, setSearch] = useState('')
  const [order, setOrder] = useState('name')
  const [isAllChecked, setAllChecked] = useState(false)
  const [isActionsClicked, setActionsClicked] = useState(false)
  const [isDeleteButtonClicked, setDeleteButtonClicked] = useState(false)
  const [isDeleteAvailable, setDeleteAvailable] = useState(true)
  const [refresh, setRefresh] = useState(false)
  const [isActionsHovered, setActionsHovered] = useState(false)
  const [page, setPage] = useState(1)
  const [reverse, setReverse] = useState('')
  const [advancedFilters, setAdvancedFiltersObj] = useState(
    getFilterStates(stateService),
  )
  const [projectList, setProjectList] = useState<Project[]>()
  const [listCount, setListCount] = useState(20)
  const [isActionsAvailable, setActionsAvailable] = useState(false)
  const [selectedItems, setSelectedItems] = useState<number[]>([])

  const projectTitlePlural = (
    CurrentUser as CurrentUserType
  ).getClientSettings().projects_language
  const projectTitleNoPlural = (
    CurrentUser as CurrentUserType
  ).getClientSettings().project_language
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color

  useEffect(() => {
    const role = CurrentUser.getRole()
    setDeleteAvailable(role !== 'client_user')
    setActionsAvailable(role !== 'supervisor' && role !== 'client_user')
  }, [])

  useEffect(() => {
    if (stateService.params.search) {
      setSearch(stateService.params.search)
    }

    if (stateService.params.pageNumber) {
      setPage(+stateService.params.pageNumber)
    }

    if (stateService.params.reverse) {
      setReverse(stateService.params.reverse)
    }

    setTimeout(() => {
      setRefresh(true)
    }, 1000)
  }, [])

  useEffect(() => {
    const getProjectsList = async () => {
      const params = {
        page: page,
      }

      if (search) params.search = search

      if (order) params.order = order

      const queryString = buildCombinedQueryString(params, advancedFilters)

      const finalQueryString = queryString

      const { data } = await Api.get('projects', finalQueryString)

      if (isAllChecked)
        setSelectedItems(data?.results.map((result) => result.id))

      setProjectList(data?.results)
      setListCount(data?.count)
      if (refresh) setRefresh(false)
    }
    getProjectsList()
  }, [order, page, advancedFilters, search, refresh])

  const setAdvancedFilters = (data) => {
    setAdvancedFiltersObj(data)
    setTimeout(() => {
      setRefresh(true)
    }, 1000)
  }

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((itemId) => itemId !== id))
    } else {
      setSelectedItems((prev) => [...prev, id])
    }
  }

  const handleSelectAll = () => {
    if (!projectList) return

    const projectIds = projectList.map((project) => project.id)

    if (!isAllChecked) {
      setSelectedItems(projectIds)
      setAllChecked(true)
    } else {
      setSelectedItems([])
      setAllChecked(false)
    }
  }

  const handleDelete = async () => {
    if (isAllChecked) {
      setPage(1)
    }

    const idsToDelete = isAllChecked ? 'all' : selectedItems.join(',')

    const params = {
      ids: idsToDelete,
      search: search,
    }

    const queryString = buildCombinedQueryString(params, advancedFilters)

    try {
      await Api.delete(`projects/bulk_delete`, queryString)
    } catch {
      console.log('error')
    }

    setRefresh(true)
    setAllChecked(false)
    setSelectedItems([])
  }

  const resetPagination = () => {
    setPage(1)
  }

  const deleteCount = isAllChecked ? listCount : selectedItems.length

  const deleteMessage = `${deleteCount} ${
    deleteCount > 1 ? projectTitlePlural : projectTitleNoPlural
  }?`

  const TABLE_HEADER_SETTINGS = [
    {
      className: 'table__header',
      name: '',
      type: 'checkbox',
      additionalStyles: {
        paddingLeft: '12px',
        paddingRight: 0,
        minWidth: '50px',
        textAlign: 'left',
        height: '30px',
        borderBottom: '1.5px solid #B3B3B3',
        paddingBottom: '4px',
        p: {
          width: '0px',
        },
        '.header-base': {
          width: '0px',
          margin: 0,
          padding: 0,
        },
        '.header-actions': {
          display: 'flex',
          gap: '7px',
          position: 'relative',
        },
        '.modal__wrapper': {
          right: '-200px',
        },
      },
      additionalInnerElement: (
        <div className="header-actions">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={handleSelectAll}
            />
            <span className="checkmark">
              <ThinCheckmark color={variableColor} />
            </span>
          </label>
          <img
            className="action"
            src={actionIcon}
            alt=""
            onClick={() => {
              setActionsClicked(!isActionsClicked)
            }}
            onMouseEnter={() => setActionsHovered(true)}
            onMouseLeave={() => setActionsHovered(false)}
          />
          {isActionsHovered && (
            <div className="actions__dropdown hover">
              <div className="hover__text">Actions</div>
            </div>
          )}
          {isActionsClicked && (
            <div className="actions__dropdown">
              {isDeleteAvailable && selectedItems.length ? (
                <div
                  className="action__text"
                  onClick={() => {
                    setActionsClicked(false)
                    setDeleteButtonClicked(true)
                  }}
                >
                  Delete
                </div>
              ) : null}
            </div>
          )}
          {isDeleteButtonClicked && selectedItems.length && (
            <ConfirmationModal
              handleClick={handleDelete}
              handleClose={() => {
                setDeleteButtonClicked(false)
              }}
              action={'Delete'}
              title={deleteMessage}
              questionMark={false}
            />
          )}
        </div>
      ),
    },
    {
      className: 'table__header name__column',
      name: 'Name',
      type: 'name',
      additionalStyles: {
        height: '30px',
        minWidth: '300px',
        textAlign: 'left',
        paddingLeft: '10px !important',
        padding: 0,
        borderBottom: '1.5px solid #B3B3B3',
        '.header-base': {
          width: 'fit-content',
          display: 'flex',
        },
        '.header-sort-arrow': {
          marginLeft: '3px',
        },
      },
      additionalInnerElement: (
        <AdvancedFilter
          fieldName={'name'}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          updateUrl={false}
          additionalFunction={resetPagination}
        />
      ),
    },
    {
      className: 'table__header',
      name: 'Active',
      type: 'is_active',
      additionalStyles: {
        height: '30px',
        padding: 0,
        borderBottom: '1.5px solid #B3B3B3',
        textAlign: 'center',
        minWidth: 'fit-content',
        '.header-base': {
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          marginRight: '20px',
        },
      },
    },
    {
      className: 'table__header',
      name: 'Number',
      type: 'number',
      additionalStyles: {
        height: '30px',
        minWidth: '139px',
        padding: 0,
        textAlign: 'left',
        borderBottom: '1.5px solid #B3B3B3',
        p: {
          display: 'flex',
          width: 'auto',
        },
      },
      additionalInnerElement: (
        <AdvancedFilter
          fieldName={'number'}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          updateUrl={false}
          additionalFunction={resetPagination}
        />
      ),
    },
    {
      className: 'table__header',
      name: 'Client Name',
      type: 'client_name',
      filterHeader: true,
      additionalStyles: {
        height: '30px',
        borderBottom: '1.5px solid #B3B3B3',
        minWidth: '150px',
        padding: 0,
        '.header-base': {
          '.header-sort-arrow': {
            marginLeft: '3px',
          },
          '.alert-button-opening': {
            marginLeft: '23px',
          },
        },
      },
      additionalInnerElement: (
        <AdvancedFilter
          fieldName={'client_name'}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          updateUrl={false}
          additionalFunction={resetPagination}
        />
      ),
    },
    {
      className: 'table__header',
      name: 'Users',
      filterHeader: false,
      additionalStyles: {
        height: '30px',
        borderBottom: '1.5px solid #B3B3B3',
        minWidth: '75px',
        padding: 0,
        '.header-base': {
          justifyContent: 'start',
          '.header-sort-arrow': {
            marginLeft: '3px',
          },
          '.alert-button-opening': {
            marginLeft: '23px',
          },
        },
      },
    },
  ]

  const isSafari = checkIsSafari()
  const baseStyle = getBaseStyle(variableColor, isSafari)

  return (
    <section css={baseStyle} className="forms_wrapper">
      <CommonPageHeader headerText={projectTitlePlural} />
      <ConfigNavBar />
      <div className="mobile__wrapper">
        <div className="content__wrapper">
          <div className="search__block">
            <Search
              search={search}
              searchCallback={setSearch}
              pageCallback={setPage}
              updateUrl={false}
              onClear={() => {
                setAllChecked(false)
              }}
            />
          </div>
          <div className="table__wrapper">
            <table>
              <TableHeaders
                headers={
                  isActionsAvailable
                    ? TABLE_HEADER_SETTINGS
                    : TABLE_HEADER_SETTINGS?.slice(1)
                }
                callback={setOrder}
                popUpLocation={false}
                updateUrl={false}
                order={order}
                setOrder={setOrder}
              />
              <tbody>
                {projectList?.map((projectData, index) => (
                  <ProjectRow
                    key={index}
                    projectData={projectData}
                    index={index}
                    handleSelect={handleSelect}
                    selectedItems={selectedItems}
                    reverse={reverse}
                    setRefresh={setRefresh}
                    page={page}
                    isActionsAvailable={isActionsAvailable}
                    search={search}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="page-switcher-base">
            <PageSwitcher
              pageLength={20}
              listCount={listCount}
              currentPage={page}
              callback={setPage}
              updateUrl={false}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

const getBaseStyle = (variableColor: string, isSafari: boolean) =>
  css({
    paddingBottom: '300px',
    td: {
      height: '40px',
      paddingRight: '10px',
      maxWidth: '150px',
    },
    '.mobile__wrapper': {
      marginLeft: '11px',
      overflowX: 'auto',
    },
    '.content__wrapper': {
      background: '#fff',
      paddingInline: '25px',
      width: 'fit-content',
    },
    '.search__block': {
      paddingTop: '20px',
      paddingBottom: '15px',
    },
    '#search': {
      width: '264px',
    },
    '.checkmark': {
      height: '22px',
      width: '22px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: '1px solid #E6E6E6',
      background: '#fff',
      svg: {
        display: 'none',
      },
    },
    '.checkmark::after': {
      display: 'none !important',
    },
    'input:checked ~ .checkmark': {
      img: {
        height: '22px',
        width: '22px',
        display: 'block',
        background: 'white',
      },
      svg: {
        height: '22px',
        width: '22px',
        display: 'block',
        background: 'white',
      },
    },
    '.table__text': {
      maxHeight: '40px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      color: '#686A6C',
      margin: 0,
      textOverflow: 'ellipsis',
    },
    '.title': {
      maxWidth: '280px',
      color: variableColor,
    },
    '.table-text-number': {
      maxWidth: '120px',
    },
    '.table-text-client': {
      maxWidth: '160px',
    },
    '.project__row': {
      color: '#575757',
      position: 'relative',
    },
    '.dark': {
      background: '#F2F3F5',
      borderBottom: '1px solid #E7EAEC',
      borderTop: '1px solid #E7EAEC',
    },
    '.checkbox-container': {
      display: 'inline-block',
      position: 'relative',
      cursor: 'pointer',
      padding: '0px 0px 22px 15px',
      margin: 0,
    },
    '.page-switcher-base': {
      paddingBottom: '10px',
    },
    '.checkmark__td': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '.name__td': {
      cursor: 'pointer',
      paddingLeft: '10px',
      color: variableColor,
    },
    '.full__name': {
      display: isSafari ? 'none' : 'block',
      position: 'absolute',
      zIndex: 1,
      border: '1px solid #D3D3D3',
      boxShadow: '2px 4px 7px 0px rgba(0, 0, 0, 0.15)',
      background: 'white',
      padding: '4px 11px',
      maxWidth: '400px',
    },
    '.title__cell__wrapper': {
      display: 'flex',
      gap: '5px',
    },
    svg: {
      cursor: 'pointer',
    },
    '.icon__td': {
      display: 'flex',
      justifyContent: 'start',
      alignItems: 'center',
      gap: '5px',
      padding: 0,
      position: 'relative',
      width: '80px',
      minWidth: '80px',

      p: {
        margin: 0,
        color: '#000000',
        minWidth: '25px',
        width: '25px',
        textAlign: 'left',
      },

      svg: {
        minWidth: '20px',
      },
    },
    '.action': {
      cursor: 'pointer',
      marginTop: '2px',
      height: '17px',
      width: '17px',
    },
    '.actions__dropdown': {
      position: 'absolute',
      zIndex: 1,
      top: '20px',
      left: '-7px',
      textAlign: 'center',
      color: '#68696D',
      background: 'white',
      boxShadow: 'rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px',
    },
    '.hover': {
      top: '25px',
      left: '7px',
    },
    '.action__text': {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      justifyContent: 'center',
      width: '83px',
      height: '24px',
      color: '#68696D',
      fontSize: '12px',
      fontWeight: 400,
    },
    '.hover__text': {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      justifyContent: 'center',
      width: '58px',
      height: '24px',
      color: '#68696D',
      fontSize: '12px',
      fontWeight: 400,
    },
    '.highlighted__item': {
      backgroundColor: hexToRGBA(variableColor),
    },
    '.table__wrapper': {
      overflowX: 'auto',
      minHeight: '600px',
    },
    '.alert-block': {
      right: '-90px !important',
    },
  })

function getFilterStates(stateService) {
  return {
    name: JSON.parse(stateService.params.name || '{}'),
  }
}
